import ErrorBoundary from "@/shared/components/error-boundary";
import { Spinner, Tab, Tabs } from "@nextui-org/react";
import Company from "./Company/Company";
import Overview from "./Overview/Overview";
import PotentialIssues from "./PotentialIssues/PotentialIssues";
import SupplyChain from "./SupplyChain/SupplyChain";

import { AgentSessionStatus, AgentSessionStepType, MultiVendorAgentSession } from '@/modules/sessions/types';
import useEffectAsync from '@/shared/hooks/useEffectAsync';
import parsePromise from '@/shared/utils/parsePromise';
import clsx from 'clsx';
import { Loader } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAgentSessionData, useAgentStepData } from '../../states';
import { VendorRiskSectionEnum } from '../../types/vendorAssessment';
import { VendorRiskResponseItemForVendorOverview } from '../../types/vendorOverviewSection';
import ReviewResponses from '../review-responses';
import { VendorRiskLevel } from './Overview/OverviewCards.types';
import RiskLevelChip from './Overview/RiskLevelChip';
import ReassessUpdateCard from "./ReassessUpdateCard";
import { useVendorRiskAssessment } from './useVendorRiskAssessment/useVendorRiskAssessment';
import { useVendorRiskAssessmentStore } from './useVendorRiskAssessment/useVendorRiskAssessmentStore';

export type VendorPageType = "OVERVIEW" | "COMPANY" | "SUPPLY_CHAIN" | "DETAILED_REVIEW" | "POTENTIAL_ISSUES";

interface Params {
  id: string;
}

const VendorRiskAssessment = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { isSaving } = useVendorRiskAssessmentStore();
  const {
    combinedVendorRiskData,
    selectedTab,
    handleTabChange,
    loadVendorRiskData,
    vendorOverview,
  } = useVendorRiskAssessment();

  const { reset } = useVendorRiskAssessmentStore();

  // Add state to track the risk level for live updates
  const [liveRiskLevel, setLiveRiskLevel] = useState<VendorRiskLevel | null>(null);
  const changesMadeRef = useRef(0);

  useEffectAsync(async () => {
    await parsePromise(loadVendorRiskData());
  }, [loadVendorRiskData]);

  // Monitor vendor overview for changes to risk_rating
  useEffect(() => {
    const riskRatingItem = vendorOverview.find(item => item.key === "risk_rating") as
      VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

    if (riskRatingItem?.value?.value) {
      // Verify risk level is a valid VendorRiskLevel value
      const riskValue = riskRatingItem.value.value;
      if (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") {
        setLiveRiskLevel(riskValue);
      }
    }
  }, [vendorOverview]);

  // Subscribe to store changes to detect remote updates
  useEffect(() => {
    const unsubscribe = useVendorRiskAssessmentStore.subscribe((state) => {
      // Only update if the changesMade value has increased (indicating a store update)
      if (state.changesMade !== changesMadeRef.current) {
        changesMadeRef.current = state.changesMade;

        // Check for updated risk rating
        const riskRatingItem = state.vendorOverview.find(item => item.key === "risk_rating") as
          VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

        if (riskRatingItem?.value?.value) {
          const riskValue = riskRatingItem.value.value;
          if (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") {
            setLiveRiskLevel(riskValue);
          }
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const [reassessUpdateCardVisible, setReassessUpdateCardVisible] = useState(true);

  const sessionData = useAgentSessionData(id);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Extract risk level calculation into a memoized value that will update from either 
  // combinedVendorRiskData or liveRiskLevel changes
  const currentRiskLevel = useMemo(() => {
    // If we have a live risk level from the direct store updates, use that
    if (liveRiskLevel) {
      return liveRiskLevel;
    }

    // Otherwise, fall back to the combinedVendorRiskData method
    if (!combinedVendorRiskData) return null;

    const riskRatingItem = combinedVendorRiskData[VendorRiskSectionEnum.vendor_overview]?.find(
      (item: any) => item.key === "risk_rating"
    ) as VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

    const riskValue = riskRatingItem?.value.value;
    return (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") ? riskValue : null;
  }, [combinedVendorRiskData, liveRiskLevel]);

  const stepData = useAgentStepData(id);
  const isReportReady =
    stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT)
      ?.status === AgentSessionStatus.COMPLETE;
  const criticality = vendorOverview.find(
        (overviewItem) => overviewItem.key === "business_criticality"
      );
  return combinedVendorRiskData ? (
    <ErrorBoundary fallback={null}>
      <div className="h-full w-full rounded-lg flex flex-col">
        {/* header */}
        <div className="bg-white sticky top-0 z-50 w-full">
          <div className="px-6 pt-4 pb-3 flex flex-row gap-3 w-full items-center bg-white">
            <div className="text-lg font-[550]">
              {(sessionData as MultiVendorAgentSession)
                .multi_file_vendor_assessment?.vendor_name ?? "Vendor Name"}
            </div>
            {currentRiskLevel && <RiskLevelChip riskLevel={currentRiskLevel} />}
            <div className="flex items-center h-7 gap-1">
              <Loader
                className={`w-4 h-4 animate-spin ${isSaving ? '' : 'invisible'}`}
              />
              {isSaving && (
                <p className="text-tiny text-[#A5A5A5] text-[10px]">Saving</p>
              )}
            </div>
          </div>

          <Tabs
            variant="underlined"
            selectedKey={selectedTab}
            classNames={{
              tab: "rounded-none px-6",
              base: "rounded-none",
              cursor: "rounded-none",
              panel: "rounded-none",
              tabContent: "rounded-none",
              tabList: "rounded-none p-0",
              wrapper: "rounded-none",
            }}
            className="border-b-1 bg-white w-full"
            onSelectionChange={(key) => handleTabChange(key as VendorPageType)}
          >
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t  after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "OVERVIEW",
              })}
              key="OVERVIEW"
              title="Overview"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t  after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "COMPANY",
              })}
              key="COMPANY"
              title="Company"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "SUPPLY_CHAIN",
              })}
              key="SUPPLY_CHAIN"
              title="Supply Chain"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "DETAILED_REVIEW",
              })}
              key="DETAILED_REVIEW"
              title="Detailed Review"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "POTENTIAL_ISSUES",
              })}
              key="POTENTIAL_ISSUES"
              title="Potential Issues"
            />
          </Tabs>
        </div>
        <div className="h-full bg-white overflow-x-auto">
          {selectedTab === "OVERVIEW" && <Overview />}
          {selectedTab === "COMPANY" && <Company />}
          {selectedTab === "SUPPLY_CHAIN" && <SupplyChain />}
          {selectedTab === "DETAILED_REVIEW" && (
            <ReviewResponses
              hideAgentTypeFromHeader={true}
              disableRoundedBorder={true}
            />
          )}
          {selectedTab === "POTENTIAL_ISSUES" && <PotentialIssues />}
        </div>
      </div>
      {!isReportReady && !criticality?.value && reassessUpdateCardVisible && (
        <ReassessUpdateCard closeFn={() => setReassessUpdateCardVisible(false)} />
      )}
    </ErrorBoundary>
  ) : (
    <div className="w-full h-full bg-white flex items-center justify-center px-24">
      <Spinner />
    </div>
  );
};

export default VendorRiskAssessment;
